import { collection, doc, addDoc, updateDoc, deleteDoc, getDocs, query, where, orderBy, serverTimestamp, getDoc, writeBatch } from 'firebase/firestore';
import { db, getCollectionPath } from '../lib/firebase';
import type { CantinaTab, CantinaTabItem } from '../types';

export const cantinaTabService = {
  // Crear una nueva mesa/cuenta
  async createTab(clubId: string, name: string, sessionId: string | null, slotNumber?: number): Promise<string> {
    const tabsRef = collection(db, getCollectionPath('cantina_tabs'));
    const docRef = await addDoc(tabsRef, {
      clubId,
      name,
      status: 'open',
      items: [],
      totalAmount: 0,
      sessionId,
      slotNumber: slotNumber || null,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
    return docRef.id;
  },

  // Obtener todas las cuentas abiertas de un club
  async getOpenTabs(clubId: string): Promise<CantinaTab[]> {
    const tabsRef = collection(db, getCollectionPath('cantina_tabs'));
    const q = query(
      tabsRef,
      where('clubId', '==', clubId),
      where('status', '==', 'open')
    );
    
    const snapshot = await getDocs(q);
    const tabs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as CantinaTab[];
    
    // Sort locally to avoid Firebase composite index requirement
    return tabs.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  },

  // Obtener una cuenta específica
  async getTab(tabId: string): Promise<CantinaTab | null> {
    const docRef = doc(db, getCollectionPath('cantina_tabs'), tabId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as CantinaTab;
    }
    return null;
  },

  // Agregar un producto a la cuenta
  async addItemToTab(tabId: string, currentItems: CantinaTabItem[], newItem: CantinaTabItem): Promise<void> {
    const docRef = doc(db, getCollectionPath('cantina_tabs'), tabId);
    
    // Ver si el item ya existe para sumar cantidad
    const existingItemIndex = currentItems.findIndex(i => i.id === newItem.id && i.price === newItem.price);
    let updatedItems = [...currentItems];
    
    if (existingItemIndex >= 0) {
      updatedItems[existingItemIndex].quantity += newItem.quantity;
    } else {
      updatedItems.push(newItem);
    }
    
    const newTotal = updatedItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
    
    await updateDoc(docRef, {
      items: updatedItems,
      totalAmount: newTotal,
      updatedAt: Date.now()
    });
  },

  // Remover o disminuir cantidad de un producto
  async removeItemFromTab(tabId: string, currentItems: CantinaTabItem[], itemId: string): Promise<void> {
    const docRef = doc(db, getCollectionPath('cantina_tabs'), tabId);
    
    const updatedItems = currentItems.filter(i => i.id !== itemId);
    const newTotal = updatedItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
    
    await updateDoc(docRef, {
      items: updatedItems,
      totalAmount: newTotal,
      updatedAt: Date.now()
    });
  },
  
  // Cambiar cantidad directamente
  async updateItemQuantity(tabId: string, currentItems: CantinaTabItem[], itemId: string, newQuantity: number): Promise<void> {
    const docRef = doc(db, getCollectionPath('cantina_tabs'), tabId);
    
    const updatedItems = currentItems.map(i => {
      if (i.id === itemId) {
        return { ...i, quantity: newQuantity };
      }
      return i;
    }).filter(i => i.quantity > 0);
    
    const newTotal = updatedItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
    
    await updateDoc(docRef, {
      items: updatedItems,
      totalAmount: newTotal,
      updatedAt: Date.now()
    });
  },

  // Cerrar y cobrar la cuenta
  async closeTab(tabId: string, paymentMethod: string, operatorEmail?: string): Promise<void> {
    const docRef = doc(db, getCollectionPath('cantina_tabs'), tabId);
    await updateDoc(docRef, {
      status: 'closed',
      paymentMethod,
      operatorEmail: operatorEmail || null,
      closedAt: Date.now(),
      updatedAt: Date.now()
    });
  },

  // Eliminar la cuenta completamente (cancelar)
  async deleteTab(tabId: string): Promise<void> {
    const docRef = doc(db, getCollectionPath('cantina_tabs'), tabId);
    await deleteDoc(docRef);
  }
};
